<script setup lang="ts">
import { DR_HATCHES, type HatchName } from "@/hatches/hatch-list";
import HatchPattern from "../HatchPattern.vue";
import { ref } from "vue";
import DRBtn from "@/components/UI/DRBtn.vue";

defineProps<{
  modelValue: HatchName | undefined;
  rotation?: number;
  // Échelle du motif telle qu'envoyée à AutoCAD. Tant qu'elle n'est pas saisie, le
  // moteur utilise la baseScale de la bibliothèque (voir NoteMenuState.computeNoteConfig) —
  // baseScale n'est affichée ici qu'en placeholder, pour indication.
  userScale?: number;
  baseScale?: number;
}>();

const emits = defineEmits<{
  (e: "update:modelValue", value: HatchName | undefined): void;
  (e: "update:rotation", value: number | undefined): void;
  (e: "update:userScale", value: number | undefined): void;
}>();

const hatchsList = DR_HATCHES;

const select = (value: HatchName | undefined) => {
  emits("update:modelValue", value);
};

const showDialog = ref(false);
</script>

<template>
  <button
    @click="showDialog = !showDialog"
    class="hatch-selector d-flex align-items-center justify-content-center gap-2"
  >
    <HatchPattern :hatchName="modelValue" :size="50" :rotation="rotation" />
    <i class="bi-chevron-down text-black-50"></i>
  </button>

  <el-dialog
    v-model="showDialog"
    :title="$t('dashboard:select.hatch.pattern')"
    width="90%"
    append-to-body
    top="1vh"
  >
    <div class=" " style="max-height: 70vh; overflow-y: auto">
      <div class="pt-3 pb-1">{{ $t("pattern") }}</div>
      <div
        class="d-flex flex-wrap gap-3 justify-content-start bordered rounded px-2 py-3"
      >
        <div
          class="cursor-pointer col-auto rounded p-2 d-flex gap-3 align-items-center hoverable-white flex-column"
          :class="{ 'bg-light text-primary': !modelValue }"
          @click="select(undefined)"
          style="width: 100px"
        >
          <HatchPattern :size="50" />
          <span class="text-center"> AUCUN </span>
        </div>
        <div
          v-for="item in hatchsList"
          :key="item.name"
          class="cursor-pointer col-auto rounded p-2 d-flex gap-3 align-items-center hoverable-white flex-column"
          :class="{ 'bg-light text-primary': item.name === modelValue }"
          @click="select(item.name)"
          style="width: 100px"
        >
          <HatchPattern :hatchName="item.name" :size="50" />
          <span class="text-center"> {{ item.name }}</span>
        </div>
      </div>
      <div class="pt-3 pb-1">Rotation (d°)</div>
      <div class="d-flex flex-wrap gap-5 mb-3 align-items-center">
        <el-input-number
          placeholder="0"
          :modelValue="rotation"
          @update:modelValue="$emit('update:rotation', $event ?? 0)"
          :min="0"
          :max="360"
          :step="10"
          :disabled="!modelValue"
          controls-position="right"
          style="width: 200px"
        >
        </el-input-number>
        <div class="d-flex flex-wrap gap-1 align-items-center">
          <el-button
            v-for="x in [0, 45, 90, 135, 180]"
            :key="x"
            class="m-0"
            :type="x !== rotation ? 'default' : 'success'"
            @click="$emit('update:rotation', x)"
            :disabled="!modelValue"
            >{{ x }}°</el-button
          >
        </div>
        <div>
          <HatchPattern
            :hatchName="modelValue"
            :size="50"
            :rotation="rotation"
          />
        </div>
      </div>
      <div class="pt-3 pb-1">{{ $t("dashboard:hatch.scale") }}</div>
      <p class="text-muted small mb-2">
        {{ $t("dashboard:hatch.scaleHelp") }}
      </p>
      <div class="d-flex flex-wrap gap-5 mb-3 align-items-center">
        <el-input-number
          :placeholder="String(baseScale ?? 1)"
          :modelValue="userScale"
          @update:modelValue="$emit('update:userScale', $event ?? undefined)"
          :min="0"
          :step="0.1"
          :precision="4"
          :disabled="!modelValue"
          controls-position="right"
          style="width: 150px"
        >
        </el-input-number>
      </div>
    </div>
    <template #footer>
      <div class="d-flex gap-2 justify-content-end">
        <DRBtn
          variant="primary"
          @click="showDialog = false"
          label="Valider"
          class="px-5"
        >
        </DRBtn>
      </div>
    </template>
  </el-dialog>
</template>

<style lang="scss" scoped>
.hatch-selector {
  border: none;
  background-color: transparent;
  border-radius: 8px;
  padding: 5px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: rgb(255, 255, 255);
    border: none;
  }
}
</style>
